'use client';

import { PhoneIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { Button } from '@components/common';
import { RecruiterCallSoftphoneModal } from './RecruiterCallSoftphoneModal';
import { useRecruiterCallEntry } from './useRecruiterCallEntry';

interface CallCandidateCardActionProps {
  /**
   * Referee's first name — always available even for locked candidates.
   * Used as the softphone popup title when the full name isn't unlocked.
   */
  candidateFirstName: string;
  /**
   * Full name — pass only when the candidate is unlocked. Preferred by
   * the popup when present; falls back to `candidateFirstName` otherwise.
   */
  candidateFullName?: string | null;
  /**
   * Card DTOs (applicant / pipeline flow) carry the candidate slug; the
   * numeric user id is not on the payload. Backend resolves the slug and
   * echoes the id back on the quote response so the softphone modal can
   * still be fed a numeric id.
   */
  candidateSlug: string;
  jobSlug: string;
  /**
   * True once the parent card has been hovered / focused at least once.
   * Gates the `/v3/recruiter-calls/quote` SWR call so an infinite-scroll
   * page doesn't fire N quote requests up-front.
   */
  hasBeenRevealed: boolean;
  /** Tailwind classes matching the sibling shortlist/message buttons. */
  buttonClassName?: string;
  /** Tailwind classes for the phone icon (same size as the sibling icons). */
  iconClassName?: string;
}

/**
 * Card-footer variant of the RecruiterCall entry point — sits next to the
 * Shortlist and Send-message buttons on applicant cards
 * (/job/[jobSlug]/applicants and /applicants/input-flow). Same softphone
 * modal as the CV-detail Contact button, only the trigger visual differs so it
 * blends into the small icon+text row.
 *
 * Fetch is gated on `hasBeenRevealed` so the quote endpoint only runs for
 * cards the user actually interacts with.
 */
export const CallCandidateCardAction = ({
  candidateFirstName,
  candidateFullName,
  candidateSlug,
  jobSlug,
  hasBeenRevealed,
  buttonClassName,
  iconClassName,
}: CallCandidateCardActionProps) => {
  const { t } = useTranslation('common');
  const {
    visible,
    priceCredits,
    resolvedCandidateUserId,
    recruiterHasHistory,
    isModalOpen,
    openModal,
    closeModal,
  } = useRecruiterCallEntry({
    candidateSlug,
    jobSlug,
    shouldFetch: hasBeenRevealed,
  });

  if (!visible || resolvedCandidateUserId === null) {
    return null;
  }

  const isFree = priceCredits === 0;
  const labelKey = isFree ? 'talent-card.v2.action.call.free' : 'talent-card.v2.action.call';
  const label = t(labelKey, { defaultValue: isFree ? 'Sună gratuit' : 'Sună' });

  return (
    <>
      <Button
        styling="text"
        size="sm"
        color="ink-medium"
        rounding="none"
        className={buttonClassName}
        onClick={openModal}
        title={label}
      >
        <PhoneIcon className={iconClassName} />
        <span>{label}</span>
      </Button>

      {isModalOpen && (
        <RecruiterCallSoftphoneModal
          open={isModalOpen}
          candidateFirstName={candidateFirstName}
          candidateFullName={candidateFullName ?? null}
          candidateUserId={resolvedCandidateUserId}
          jobSlug={jobSlug}
          priceCredits={priceCredits}
          showOnboarding={!recruiterHasHistory}
          onClose={closeModal}
        />
      )}
    </>
  );
};
